import styled from 'styled-components';
import * as animations from '../../styles/animations';

export const IntegrationsSection = styled.section<{ $isVisible?: boolean }>`
  padding: 120px 0;
  background: linear-gradient(180deg, #0a0a0a 0%, #0a0a0a 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, transparent 0%, rgba(10, 10, 10, 0.2) 20%, rgba(10, 10, 10, 0.5) 40%, rgba(10, 10, 10, 0.8) 70%, rgba(10, 10, 10, 1) 100%);
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

export const IntegrationsNetwork = styled.div<{ $isVisible?: boolean }>`
  margin: 40px 0;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: ${props => props.$isVisible ? 'translateY(0)' : 'translateY(30px)'};
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const NeuralNetworkBackground = styled.div<{ $isVisible?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: ${props => props.$isVisible ? 0.1 : 0};
  transition: opacity 1s ease;
  pointer-events: none;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 68, 68, 0.1) 0%, transparent 70%);
    transform: translate(-50%, -50%);
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.1; }
    50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.2; }
  }
`;

export const IntegrationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 0 16px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
`;

export const IntegrationCard = styled.div<{ $available?: boolean; $isVisible?: boolean; $delay?: number }>`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: 24px;
  position: relative;
  overflow: hidden;
  will-change: opacity, filter, transform, border-color, box-shadow;
  opacity: ${props => props.$isVisible ? 1 : 0};
  filter: blur(${props => props.$isVisible ? '0px' : '12px'});
  transform: ${props => props.$isVisible ? 'translateY(0) scale(1) rotateX(0deg)' : 'translateY(50px) scale(0.85) rotateX(10deg)'};
  transition: opacity 1.6s cubic-bezier(0.16, 1, 0.3, 1),
              filter 1.6s cubic-bezier(0.16, 1, 0.3, 1),
              transform 1.6s cubic-bezier(0.16, 1, 0.3, 1),
              border-color 0.4s ease,
              box-shadow 0.4s ease;
  transition-delay: ${props => props.$delay ? `${props.$delay}ms` : '0ms'};
  cursor: pointer;
  perspective: 1000px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$available ?
    'linear-gradient(90deg, #ff4444, #dc2626)' :
    'linear-gradient(90deg, #f97316, #ea580c)'};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(circle, rgba(255, 68, 68, 0.1) 0%, transparent 70%);
    transform: translate(-50%, -50%);
    transition: all 0.4s ease;
    border-radius: ${props => props.theme.borders.radius.full};
  }

  &:hover {
    border-color: ${props => props.$available ? 'rgba(255, 68, 68, 0.4)' : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);

    &::before {
      opacity: 1;
    }

    &::after {
      width: 200px;
      height: 200px;
    }
  }

  @media (max-width: 768px) {
    padding: 20px;

    &:hover {
      transform: translateY(-4px) scale(1.01);
    }
  }
`;

export const IntegrationCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

export const IntegrationCardIcon = styled.div<{ $available?: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: ${props => props.theme.borders.radius.lg};
  background: ${props => props.$available ?
    'linear-gradient(135deg, rgba(255, 68, 68, 0.1), rgba(220, 38, 38, 0.05))' :
    'linear-gradient(135deg, rgba(100, 116, 139, 0.1), rgba(71, 85, 105, 0.05))'};
  border: 1px solid ${props => props.$available ?
    'rgba(255, 68, 68, 0.2)' :
    'rgba(100, 116, 139, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${props => props.$available ?
    'linear-gradient(45deg, #ff4444, #dc2626, #ff4444)' :
    'linear-gradient(45deg, #64748b, #475569, #64748b)'};
    border-radius: ${props => props.theme.borders.radius.lg};
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
    border-radius: ${props => props.theme.borders.radius.base};
    transition: transform 0.3s ease;
  }

  ${IntegrationCard}:hover & {
    transform: scale(1.1) rotate(5deg);

    &::before {
      opacity: 0.3;
    }

    img {
      transform: scale(1.1);
    }
  }
`;

export const IntegrationCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const IntegrationCardName = styled.h3<{ $available?: boolean }>`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.$available ? '#ffffff' : '#9ca3af'};
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

export const IntegrationCardStatus = styled.span<{ $available?: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$available ? '#ff4444' : '#64748b'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const IntegrationCardDescription = styled.p`
  font-size: 14px;
  color: #a1a1aa;
  line-height: 1.5;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const IntegrationPreview = styled.div<{ $available?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${props => props.$available ?
    'rgba(255, 68, 68, 0.1)' :
    'rgba(100, 116, 139, 0.1)'};
  border: 1px solid ${props => props.$available ?
    'rgba(255, 68, 68, 0.2)' :
    'rgba(100, 116, 139, 0.2)'};
  border-radius: ${props => props.theme.borders.radius.base};
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$available ? '#ff4444' : '#64748b'};
  transition: all 0.3s ease;

  ${IntegrationCard}:hover & {
    background: ${props => props.$available ?
    'rgba(255, 68, 68, 0.2)' :
    'rgba(100, 116, 139, 0.2)'};
    transform: translateY(-2px);
  }
`;

export const PreviewIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.6;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 6px;
    height: 6px;
    background: white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }
`;
